import { TIMESTAMP } from '@@types/common';
import Member from '@@types/Member';
import Vacation from '@@types/Vacation';

import { DAY } from '~/constants/date';
import { assignTime, toTimestamp } from '~/utils/dateUtil';
import { isLeaver } from '~/utils/memberUtil';
import moment from 'moment-timezone';
import { all, any, curry, flatten, identity, reduce } from 'ramda';

type TimestampRange = [number, number];

const ONE_DAY = 24 * 60 * 60 * 1000;

class MemberOfficeHours {
  private cache: {
    valid: boolean;
    until: number;
    key: string;
  } | null = null;

  public isInOfficeHour = (member: Member): boolean => {
    const now = Date.now();
    if (this.isCacheValid(member, now)) {
      return this.cache.valid;
    }

    const { valid = [], notValid = [] } = this.getMemberTimestampRange(member);
    if (valid.length === 0) {
      this.cache = {
        valid: true,
        until: now + ONE_DAY,
        key: MemberOfficeHours.makeCacheKey(member),
      };
      return true;
    }
    this.cache = {
      valid: all(MemberOfficeHours.isBetweenTime(now), valid) && !any(MemberOfficeHours.isBetweenTime(now), notValid),
      until: this.getCloseTimestamp(now, valid.concat(notValid)),
      key: MemberOfficeHours.makeCacheKey(member),
    };
    return this.cache.valid;
  };

  get cacheValidTime() {
    return this.cache?.until ?? Date.now() + ONE_DAY;
  }

  public static calcClosedTimestamp = curry((now: number, timestampList: number[]): number => {
    return reduce((min: number, value: number) => {
      return now < value && value < min ? value : min;
    }, now + 1000 * 60 * 60 * 24)(timestampList);
  });

  private isCacheValid(member: Member, now: number) {
    return this.cache && this.cache.until > now && MemberOfficeHours.makeCacheKey(member) === this.cache.key;
  }

  private static makeCacheKey(member: Member) {
    return `${member?.timezoneName}${member?.officeHours?.begin}${member?.officeHours?.end}${member?.vacation?.displayProfileFlag}${member?.vacation?.startedAt}${member?.vacation?.endedAt}`;
  }

  private getCloseTimestamp = (now: number, timestampRangeList: TimestampRange[]): TIMESTAMP => {
    return MemberOfficeHours.calcClosedTimestamp(now, flatten(timestampRangeList));
  };

  private static isBetweenTime = curry((now: number, [begin, end]: TimestampRange) => {
    return begin <= now && now <= end;
  });

  private getMemberTimestampRange = (member: Member): { valid?: TimestampRange[]; notValid?: TimestampRange[] } => {
    const { officeHours, timezoneName, vacation } = member;
    if (isLeaver(member)) {
      return {};
    }

    return {
      valid: [this.getOfficeHourRange({ timezoneName, officeHours })].filter(identity),
      notValid: [this.getWeekendRange(timezoneName), this.getVacationRange(vacation)].filter(identity),
    };
  };

  private getWeekendRange = (timezoneName?: string): TimestampRange | null => {
    if (!timezoneName) {
      return null;
    }
    const start = moment.tz(timezoneName).day(6).startOf('day').valueOf(); // 토요일 시작
    const end = start + DAY + DAY - 1; // 일요일 끝
    return [start, end];
  };

  private getVacationRange = (vacation?: Vacation): TimestampRange | null => {
    if (!vacation || !vacation.displayProfileFlag) {
      return null;
    }
    return [toTimestamp(vacation.startedAt), toTimestamp(vacation.endedAt)];
  };

  private getOfficeHourRange = ({
    timezoneName,
    officeHours,
  }: {
    timezoneName?: string;
    officeHours?: { begin?: string; end?: string };
  }): TimestampRange | null => {
    if (
      !timezoneName ||
      !officeHours ||
      !officeHours.begin ||
      !officeHours.end ||
      officeHours.begin === officeHours.end
    ) {
      return null;
    }
    const now = moment.tz(timezoneName);
    const begin = assignTime(now, officeHours.begin).startOf('minute').valueOf();
    const end = assignTime(now, officeHours.end).endOf('minute').valueOf();
    return begin < end ? [begin, end] : [begin, end + DAY];
  };
}

export default MemberOfficeHours;
